import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Highlighter, PenLine, Trash2 } from 'lucide-react';
import { Slider } from '../../ui/slider';
import {
  MAX_ROUTE_CORRIDOR,
  MAX_ROUTE_THICKNESS,
  MIN_ROUTE_CORRIDOR,
  MIN_ROUTE_THICKNESS,
} from '../route-line';
import { PopoverPanel, PopoverRoundButton, PopoverRow } from './popover-parts';

export interface LineActionsLabels {
  thickness: string;
  corridor: string;
  deletePoint: string;
}

interface LineActionsProps {
  thickness: number;
  corridor: number;
  onThickness: (value: number) => void;
  onCorridor: (value: number) => void;
  onDelete: () => void;
  /** Offer the corridor. Off on rock, where the line is the whole topo. */
  allowCorridor?: boolean;
  labels: LineActionsLabels;
  testId?: string;
}

/**
 * What can be done to the point in hand, at the point.
 *
 * The holds' twin (see `HoldActions`) and the same two states, but the open one
 * is not a list of answers — a line has only two things to set, and both of them
 * are a quantity. So it unfolds into the two sliders themselves rather than into
 * rows that would each open a slider of their own: the setter is looking at the
 * line while dragging them, and every press between them and the picture is one
 * they have to make twice.
 *
 * Small, the two measurements are icons that open exactly that.
 */
export function LineActions({
  thickness,
  corridor,
  onThickness,
  onCorridor,
  onDelete,
  allowCorridor = true,
  labels,
  testId,
}: LineActionsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (open) {
    return (
      <PopoverPanel testId={testId} className="w-60 gap-2 p-3">
        <SliderField
          label={labels.thickness}
          value={thickness}
          min={MIN_ROUTE_THICKNESS}
          max={MAX_ROUTE_THICKNESS}
          step={0.001}
          onChange={onThickness}
          format={(value) => `${(value * 100).toFixed(1)}%`}
          testId="line-thickness"
        />

        {allowCorridor && (
          <SliderField
            label={labels.corridor}
            value={corridor}
            min={MIN_ROUTE_CORRIDOR}
            max={MAX_ROUTE_CORRIDOR}
            step={0.01}
            onChange={onCorridor}
            format={(value) => `${Math.round(value * 100)}%`}
            testId="line-corridor"
          />
        )}

        <div className="flex flex-col gap-0.5 border-t pt-1">
          <PopoverRow
            label={labels.deletePoint}
            onClick={onDelete}
            destructive
            icon={
              <span className="flex size-7 items-center justify-center">
                <Trash2 className="size-4" aria-hidden />
              </span>
            }
          />
          <PopoverRow
            label={t('climbEditor.actions.close')}
            onClick={() => setOpen(false)}
            icon={
              <span className="flex size-7 items-center justify-center">
                <ChevronUp className="size-4" aria-hidden />
              </span>
            }
          />
        </div>
      </PopoverPanel>
    );
  }

  return (
    <PopoverPanel testId={testId} className="flex-row items-center gap-1 rounded-full p-1">
      {/* The two measurements are set in the open state, so small they are the
          way into it — pressing the one wanted lands on the slider for it. */}
      <PopoverRoundButton label={labels.thickness} onClick={() => setOpen(true)}>
        <PenLine className="size-4" aria-hidden />
      </PopoverRoundButton>

      {allowCorridor && (
        <PopoverRoundButton label={labels.corridor} onClick={() => setOpen(true)}>
          <Highlighter className="size-4" aria-hidden />
        </PopoverRoundButton>
      )}

      <PopoverRoundButton label={labels.deletePoint} onClick={onDelete}>
        <Trash2 className="size-4" aria-hidden />
      </PopoverRoundButton>

      <PopoverRoundButton label={t('climbEditor.actions.more')} onClick={() => setOpen(true)}>
        <ChevronDown className="size-4" aria-hidden />
      </PopoverRoundButton>
    </PopoverPanel>
  );
}

/**
 * One measurement: its name, what it is worth right now, and the bar that sets
 * it. The number is there because both of these are a soft edge on a
 * photograph, where a step or two is hard to be sure of by eye.
 */
function SliderField({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
  testId,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  format: (value: number) => string;
  testId: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium">{label}</span>
        <span className="text-xs tabular-nums text-muted-foreground">{format(value)}</span>
      </div>
      <Slider
        aria-label={label}
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([next]) => onChange(next)}
      />
    </div>
  );
}
